import { useEffect } from 'react';

/**
 * Freezes the page behind an open overlay.
 *
 * Every modal, drawer and slide-over in the app renders its own `fixed inset-0` scrim, and
 * none of them stopped the document underneath from scrolling. On a phone that reads as a
 * bug: you open "Invite member", drag inside the form, and the chambers page slides around
 * behind it — sideways as well as up and down, because nothing pinned it.
 *
 * Why `position: fixed` and not just `overflow: hidden`:
 * iOS Safari honours `overflow: hidden` on the body for a scrollbar it does not have, but
 * still happily scrolls the document on a touch drag. Pinning the body and offsetting it by
 * the current scroll position is the technique that actually holds there — the page cannot
 * move because it is no longer in flow.
 *
 * The document is the scroll container in this app (`AppShell` is `min-h-screen`, and `main`
 * is deliberately `overflow-x-clip` rather than `overflow-hidden` so it never becomes one),
 * so the body is the right element to pin.
 *
 * Reference-counted at module level: a confirm dialog opening on top of a modal takes a
 * second lock, and the page only comes back when the last one is released. Without the
 * count, closing the inner dialog would unfreeze the page while the outer modal is still
 * open.
 */

let lockCount = 0;
let release: (() => void) | null = null;

function engage() {
  if (lockCount++ > 0) return;

  const body = document.body;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  const previous = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
    paddingRight: body.style.paddingRight,
  };

  // Pinning the body removes the document's scrollbar, which on a desktop would let the
  // whole page jump ~15px to the right the moment a modal opens. Hold that width back as
  // padding so nothing shifts. On a phone and on overlay scrollbars the gap is 0 and this
  // is a no-op.
  const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;

  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.left = `-${scrollX}px`;
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  if (scrollbarGap > 0) body.style.paddingRight = `${scrollbarGap}px`;

  release = () => {
    Object.assign(body.style, previous);
    // `html` carries `scroll-behavior: smooth`, so a plain scrollTo would animate the page
    // back to where it already was — visible as a lurch on close. 'instant' overrides it.
    window.scrollTo({ left: scrollX, top: scrollY, behavior: 'instant' as ScrollBehavior });
  };
}

function disengage() {
  if (lockCount === 0) return;
  if (--lockCount > 0) return;
  release?.();
  release = null;
}

/**
 * Locks page scroll for as long as the calling component is mounted.
 *
 * Mount it with the overlay itself — `<ScrollLock />` inside the scrim — rather than calling
 * it from a component that merely *can* show an overlay, so the lock's lifetime is exactly
 * the overlay's lifetime.
 *
 * @param active pass `false` to hold the lock off (for a component that stays mounted while
 *               its overlay is closed).
 */
export function useScrollLock(active: boolean = true) {
  useEffect(() => {
    if (!active) return;
    engage();
    return disengage;
  }, [active]);
}
